import {
  DragEvent,
  KeyboardEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import styled, { css } from 'styled-components';

import type { ChatSession } from '@paradigm/terminal-core';

import { useLongPress } from './hooks';
import { PinIcon } from '../icons';
import { DEFAULT_FOLDER_ID, SESSION_DRAG_TYPE } from './constants';
import { MenuItem, MenuPositioned, initialClamp } from './menu';
import { toLocalPosition } from './menu-position';
import ConfirmDelete from './ConfirmDelete';

interface SessionRowProps {
  readonly session: ChatSession;
  readonly active: boolean;
  readonly searchQuery: string;
  readonly moveTargets: ReadonlyArray<{ id: string; name: string }>;
  readonly onSelect: (sessionId: string) => void;
  readonly onRename: (sessionId: string, title: string) => void;
  readonly onPin: (sessionId: string, pinned: boolean) => void;
  readonly onDelete: (sessionId: string) => void;
  readonly onMove: (sessionId: string, folderId: string) => void;
}

export default function SessionRow({
  session,
  active,
  searchQuery,
  moveTargets,
  onSelect,
  onRename,
  onPin,
  onDelete,
  onMove,
}: SessionRowProps) {
  const [renaming, setRenaming] = useState(false);
  const [renameDraft, setRenameDraft] = useState(session.title);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const closeMenu = useCallback(() => setMenu(null), []);

  useEffect(() => {
    if (menu == null) return;
    function onClickAway(e: globalThis.MouseEvent) {
      const target = e.target as HTMLElement;
      if (target.closest('[data-context-menu]') != null) return;
      // The ellipsis toggles the menu itself; ignore its mousedown here so the
      // click-away doesn't close-then-reopen (leaving it stuck open).
      if (target.closest('[data-menu-toggle]') != null) return;
      closeMenu();
    }
    function onEsc(e: globalThis.KeyboardEvent) {
      if (e.key !== 'Escape') return;
      // Claim the key — see AccountMenu: an open menu is the top layer, so one
      // press must not also reach a modal listening underneath it.
      e.stopPropagation();
      closeMenu();
    }
    window.addEventListener('mousedown', onClickAway, true);
    window.addEventListener('keydown', onEsc, true);
    return () => {
      window.removeEventListener('mousedown', onClickAway, true);
      window.removeEventListener('keydown', onEsc, true);
    };
  }, [menu, closeMenu]);

  useEffect(() => {
    if (!renaming) return;
    inputRef.current?.focus();
    inputRef.current?.select();
  }, [renaming]);

  const startRename = useCallback(() => {
    setRenameDraft(session.title);
    setRenaming(true);
    closeMenu();
  }, [session.title, closeMenu]);

  const commitRename = useCallback(() => {
    const trimmed = renameDraft.trim();
    if (trimmed.length > 0 && trimmed !== session.title) {
      onRename(session.sessionId, trimmed);
    }
    setRenaming(false);
  }, [renameDraft, session.sessionId, session.title, onRename]);

  const cancelRename = useCallback(() => {
    setRenameDraft(session.title);
    setRenaming(false);
  }, [session.title]);

  // Long-press to open the context menu on touch. The menu's portal
  // clamps the coords into the viewport, so anchoring at the touch
  // point works on phones and within the docked drawer alike.
  const { touchHandlers, consumeFired } = useLongPress(
    useCallback(
      ({ x, y }: { x: number; y: number }) => {
        if (renaming || confirmingDelete) return;
        setMenu({ x, y });
      },
      [renaming, confirmingDelete],
    ),
  );

  const handleRowClick = useCallback(() => {
    if (renaming || confirmingDelete) return;
    // Lifting the finger after a long-press fires a synthesized click —
    // skip it so we don't navigate into the chat we just opened the
    // menu for.
    if (consumeFired()) return;
    onSelect(session.sessionId);
  }, [renaming, confirmingDelete, session.sessionId, onSelect, consumeFired]);

  const handleContextMenu = useCallback((e: MouseEvent) => {
    e.preventDefault();
    setMenu({ x: e.clientX, y: e.clientY });
  }, []);

  const handleRenameKey = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        commitRename();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        cancelRename();
      }
    },
    [commitRename, cancelRename],
  );

  const handleEllipsis = useCallback((e: MouseEvent) => {
    e.stopPropagation();
    const el = e.currentTarget as HTMLElement;
    setMenu((prev) => {
      // Second click on the dots closes the open menu (toggle).
      if (prev != null) return null;
      const rect = el.getBoundingClientRect();
      // Anchor the menu's top-right at the button's bottom-right so it
      // opens leftward (and downward) — keeps it visually attached to
      // the dots button and inside the terminal panel rather than
      // spilling rightward into the page underneath when the drawer
      // is floating or docked to the right edge.
      const approxMenuWidth = 140;
      return { x: rect.right - approxMenuWidth, y: rect.bottom };
    });
  }, []);

  // Native HTML5 drag — pointing at a folder drop zone fires the move via
  // `RecentsView`'s `handleMoveSession`. The custom MIME (lower-cased per the
  // dnd spec) lets folder drop zones reject foreign drags by sniffing
  // `dataTransfer.types` on `dragover`, without reading the payload.
  const handleDragStart = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      if (renaming || confirmingDelete) {
        e.preventDefault();
        return;
      }
      e.dataTransfer.setData(SESSION_DRAG_TYPE, session.sessionId);
      e.dataTransfer.setData('text/plain', session.title);
      e.dataTransfer.effectAllowed = 'move';
      setDragging(true);
    },
    [renaming, confirmingDelete, session.sessionId, session.title],
  );

  const handleDragEnd = useCallback(() => {
    setDragging(false);
  }, []);

  return (
    <Row
      // A stable hook for tests, like `[data-account-button]`: the row has no
      // role or accessible name of its own, so the only other way to find one
      // is by its title text — which collides with the transcript's.
      data-session-row={session.sessionId}
      $active={active}
      $dragging={dragging}
      draggable={!renaming && !confirmingDelete}
      onClick={handleRowClick}
      onContextMenu={handleContextMenu}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      {...touchHandlers}
    >
      {session.pinned ? (
        <PinSlot>
          <PinIcon />
        </PinSlot>
      ) : null}
      <RowBody>
        {renaming ? (
          <RenameInput
            ref={inputRef}
            value={renameDraft}
            onChange={(e) => setRenameDraft(e.target.value)}
            onKeyDown={handleRenameKey}
            onBlur={commitRename}
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <RowTitle $dim={searchQuery.length > 0} title={session.title}>
            <HighlightedTitle title={session.title} query={searchQuery} />
          </RowTitle>
        )}
      </RowBody>
      {confirmingDelete ? (
        <ConfirmDelete
          onConfirm={(e) => {
            e.stopPropagation();
            onDelete(session.sessionId);
          }}
          onCancel={(e) => {
            e.stopPropagation();
            setConfirmingDelete(false);
          }}
        />
      ) : (
        <EllipsisButton
          type="button"
          aria-label="Session actions"
          data-menu-toggle
          onClick={handleEllipsis}
        >
          ⋮
        </EllipsisButton>
      )}

      {menu != null ? (
        <ContextMenu
          x={menu.x}
          y={menu.y}
          pinned={session.pinned}
          moveTargets={moveTargets}
          currentFolderId={session.customFolderId}
          onPin={() => {
            onPin(session.sessionId, !session.pinned);
            closeMenu();
          }}
          onRename={startRename}
          onMove={(folderId) => {
            onMove(session.sessionId, folderId);
            closeMenu();
          }}
          onDelete={() => {
            setConfirmingDelete(true);
            closeMenu();
          }}
        />
      ) : null}
    </Row>
  );
}

function ContextMenu({
  x,
  y,
  pinned,
  moveTargets,
  currentFolderId,
  onPin,
  onRename,
  onMove,
  onDelete,
}: {
  readonly x: number;
  readonly y: number;
  readonly pinned: boolean;
  readonly moveTargets: ReadonlyArray<{ id: string; name: string }>;
  readonly currentFolderId: string | undefined;
  readonly onPin: () => void;
  readonly onRename: () => void;
  readonly onMove: (folderId: string) => void;
  readonly onDelete: () => void;
}) {
  const [view, setView] = useState<'main' | 'move'>('main');
  // Render through a portal to document.body so the menu escapes any
  // ancestor that establishes a containing block for fixed positioning
  // (the floating terminal panel uses `backdrop-filter`, which makes
  // `position: fixed` resolve against the panel and `overflow: hidden`
  // clip the menu otherwise — exact bug the user hit when unpinned).
  const ref = useRef<HTMLDivElement>(null);
  const [clamped, setClamped] = useState<{ x: number; y: number }>(() =>
    initialClamp(x, y),
  );

  // Re-clamp synchronously after layout (before paint) so the user never
  // sees the menu pop in at the unclamped position. The docked panel
  // hugs the right edge, so the unflipped menu usually overflows — this
  // is the bug where the dropdown landed outside the view.
  useLayoutEffect(() => {
    const el = ref.current;
    if (el == null) return;
    const rect = el.getBoundingClientRect();
    const margin = 8;
    const maxX = window.innerWidth - rect.width - margin;
    const maxY = window.innerHeight - rect.height - margin;
    // Clamped in screen space (rect and innerWidth/innerHeight agree there),
    // then converted into the element's own space — see menu-position: under UI
    // scale a screen coordinate written straight to left/top is scaled twice.
    setClamped(
      toLocalPosition(
        el,
        Math.max(margin, Math.min(x, maxX)),
        Math.max(margin, Math.min(y, maxY)),
      ),
    );
  }, [x, y]);

  // The chat's current folder is hidden from the Move-to list entirely
  // — there's no use case for "move to where I already am" so we keep
  // the dropdown to actionable targets only. Unfiled sessions map to
  // the synthetic default folder id.
  const effectiveCurrentFolderId = currentFolderId ?? DEFAULT_FOLDER_ID;
  const visibleMoveTargets = moveTargets.filter(
    (t) => t.id !== effectiveCurrentFolderId,
  );

  return createPortal(
    <MenuPositioned
      ref={ref}
      data-context-menu
      style={{ left: clamped.x, top: clamped.y }}
    >
      {view === 'main' ? (
        <>
          <MenuItem
            type="button"
            onClick={(e: MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              onPin();
            }}
          >
            {pinned ? 'Unpin' : 'Pin'}
          </MenuItem>
          <MenuItem
            type="button"
            onClick={(e: MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              onRename();
            }}
          >
            Rename
          </MenuItem>
          <MenuItem
            type="button"
            onClick={(e: MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              setView('move');
            }}
          >
            Move to Folder
          </MenuItem>
          <MenuItem
            $danger
            type="button"
            onClick={(e: MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              onDelete();
            }}
          >
            Delete
          </MenuItem>
        </>
      ) : (
        <>
          <MenuItem
            type="button"
            onClick={(e: MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              setView('main');
            }}
          >
            ← Back
          </MenuItem>
          {visibleMoveTargets.map((t) => (
            <MenuItem
              key={t.id}
              type="button"
              onClick={(e: MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                onMove(t.id);
              }}
            >
              {t.name}
            </MenuItem>
          ))}
        </>
      )}
    </MenuPositioned>,
    document.body,
  );
}

function HighlightedTitle({
  title,
  query,
}: {
  readonly title: string;
  readonly query: string;
}) {
  if (query.length === 0) return <>{title}</>;
  const lowerTitle = title.toLowerCase();
  const segments: Array<{ text: string; matched: boolean }> = [];
  let cursor = 0;
  while (cursor < title.length) {
    const idx = lowerTitle.indexOf(query, cursor);
    if (idx === -1) {
      segments.push({ text: title.slice(cursor), matched: false });
      break;
    }
    if (idx > cursor) {
      segments.push({ text: title.slice(cursor, idx), matched: false });
    }
    segments.push({
      text: title.slice(idx, idx + query.length),
      matched: true,
    });
    cursor = idx + query.length;
  }
  return (
    <>
      {segments.map((s, i) =>
        s.matched ? (
          // Segments are recomputed per render from `title` + `query`;
          // they have no stable id, so the array index is acceptable
          // (the list shape is deterministic for a given title/query).
          // eslint-disable-next-line react/no-array-index-key
          <Highlight key={`m-${i}-${s.text}`}>{s.text}</Highlight>
        ) : (
          s.text
        ),
      )}
    </>
  );
}

// Container for a flat list of session rows. Also the base for the
// indented in-folder list (`FolderSessionList` in FolderRow.tsx).
export const SessionList = styled.div`
  display: flex;
  flex-direction: column;
`;

const PinSlot = styled.div`
  width: 16px;
  display: flex;
  justify-content: center;
  flex-shrink: 0;
`;

const RowBody = styled.div`
  flex: 1;
  min-width: 0;
  position: relative;
  display: flex;
  align-items: center;
`;

const RowTitle = styled.div<{ $dim: boolean }>`
  font-size: 12px;
  font-weight: 400;
  color: ${(p) => (p.$dim ? 'var(--text-label)' : 'var(--text-primary)')};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Highlight = styled.span`
  color: var(--text-primary);
`;

const Row = styled.div<{ $active: boolean; $dragging: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 6px;
  padding: 9px 12px;
  margin: 0;
  border-radius: 6px;
  cursor: pointer;
  color: var(--text-primary);

  ${(p) =>
    p.$active &&
    css`
      background: var(--hover-bg);
    `}

  ${(p) =>
    p.$dragging &&
    css`
      opacity: 0.4;
    `}

  &:hover {
    background: var(--hover-bg);
  }

  &:hover > button[aria-label='Session actions'] {
    opacity: 1;
  }
`;

const RenameInput = styled.input`
  padding: 2px 4px;
  border-radius: 4px;
  border: 1px solid var(--accent-color);
  background: var(--bg-color-container);
  color: var(--text-primary);
  font-size: 12px;
  font-family: inherit;
  outline: none;
  width: 100%;
`;

const EllipsisButton = styled.button`
  border: none;
  background: transparent;
  color: var(--text-secondary);
  font-size: 14px;
  line-height: 1;
  padding: 2px 6px;
  border-radius: 4px;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.1s ease;
  flex-shrink: 0;

  &:hover {
    color: var(--text-primary);
    background: var(--hover-bg);
  }

  &:focus-visible {
    opacity: 1;
  }
`;
